import {
  deriveClickIdProps,
  derivePriceProps,
} from '@dupe/common/analyticsCommerce'
import {
  deriveSnakeCaseTwins,
  toSnakeCase,
} from '@dupe/common/analyticsPropertyNames'
import { deriveMerchantKey } from '@dupe/common/merchantKey'
import { HOLDOUT_FLAG, holdoutFlagFor } from '@dupe/common/holdout'
import { datadogRum } from '@datadog/browser-rum'
import { getCookie } from 'cookies-next'
import { usePlausible } from 'next-plausible'
import { usePostHog } from 'posthog-js/react'
import { useCallback, useEffect, useRef } from 'react'
import { useIntercom } from 'react-use-intercom'
import {
  type ChatExperience,
  useChatExperience,
} from '~/src/context/ChatExperienceProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import {
  generateAnalyticsEventId,
  isZarazTrackEnabled,
  zarazTrack,
} from '~/src/lib/analytics/zaraz'
import {
  stashPendingPersonSetOnce,
  takePendingPersonSetOnce,
} from '~/src/lib/analytics/pendingPersonProps'
import { stringToHash } from '~/src/lib/urlHelpers'
import { useIntercomActivationStore } from '~/src/stores/useIntercomActivationStore'

type IntercomUpdateArgs = Parameters<
  ReturnType<typeof useIntercom>['update']
>[0]

/**
 * Properties that are FIRST-PARTY ONLY — they reach PostHog and Tinybird, and
 * are stripped before GA/Zaraz, the Meta pixel and Plausible.
 *
 * These are the free-text strings the shopper typed. A product title or a shop
 * key is a fact about our catalogue; a search query is whatever was on someone's
 * mind, and on the v2 chat surface it is conversational rather than a two-word
 * product name. It can carry health details, names, addresses — and we have no
 * redaction on this path (`packages/common/piiScrub.ts` exists but is not wired
 * into web analytics).
 *
 * We still need the text for lifecycle: "here are cheaper versions of the chair
 * you searched for" is the whole point. PostHog and Tinybird are where that is
 * read from. Nothing downstream of GA, Meta or Plausible uses it, so sending it
 * there buys nothing and hands free-text user input to three third parties.
 *
 * Raised by an adversarial review of PR #4630 as the search-outcome work took
 * `q` coverage from 27% of searches to ~98%. The exposure predates that PR —
 * the homepage and chat-input emitters have always sent `q` to these sinks —
 * so this strips it everywhere, not just on the new emitter.
 *
 * ⚠️ Add a key here rather than dropping the property: absent from PostHog
 * would break the lifecycle rail this exists to feed. Each key's snake_case
 * twin (see `deriveSnakeCaseTwins`) is stripped with it automatically.
 */
const FIRST_PARTY_ONLY_KEYS: readonly string[] = [
  ...new Set(
    ['q', 'query', 'searchQuery'].flatMap((key) => [key, toSnakeCase(key)])
  ),
]

/** `first_search_*` carries the shopper's search text; see firstSearchMemory.ts. */
const isFirstPartyOnly = (key: string): boolean =>
  FIRST_PARTY_ONLY_KEYS.includes(key) || key.startsWith('first_search_')

/**
 * The payload minus the first-party-only keys. Returns the SAME object when
 * there is nothing to strip, so the overwhelming majority of events don't pay
 * for a copy.
 */
const stripFirstPartyOnly = (
  properties: Record<string, any> | undefined
): Record<string, any> | undefined => {
  if (!properties) return properties
  if (!Object.keys(properties).some(isFirstPartyOnly)) {
    return properties
  }
  const out: Record<string, any> = {}
  for (const [key, value] of Object.entries(properties)) {
    if (!isFirstPartyOnly(key)) out[key] = value
  }
  return out
}

const useAnalytics = () => {
  const plausible = usePlausible()
  const posthog = usePostHog()
  const { update } = useIntercom()
  const { tempUserId } = useGetTempUserId()
  const { authUser } = useAuthUser()
  // Intercom activates lazily; identify() can fire (on auth resolve) before
  // the messenger boots, and react-use-intercom drops pre-boot updates. Hold
  // the latest identity payload and replay it once booted so support context
  // isn't lost for sessions that activate Intercom later.
  const intercomBooted = useIntercomActivationStore((s) => s.booted)
  const pendingIntercomUpdateRef = useRef<IntercomUpdateArgs | undefined>(
    undefined
  )

  useEffect(() => {
    if (!intercomBooted || !pendingIntercomUpdateRef.current) return
    update(pendingIntercomUpdateRef.current)
    pendingIntercomUpdateRef.current = undefined
  }, [intercomBooted, update])
  // When a track call happens inside `<ChatExperienceProvider>` (the chats entry
  // or BrainChatPage), tag every event with the A/B bucket so the analytics
  // team can slice any event by cohort without having to reason about route
  // shape. Returns null outside the provider, in which case we inject nothing.
  const chatExperience = useChatExperience()

  const track = useCallback(
    (
      event: string,
      properties?: Record<string, any>,
      options?: {
        bypassAuth?: boolean
        skipGA?: boolean
        skipPosthog?: boolean
        sendToPlausible?: boolean
        sendToTinybird?: boolean
        sendToFb?: boolean
        sendToPodscribe?: boolean
        /** PostHog-only person properties, applied with this event. */
        personSet?: Record<string, unknown>
        personSetOnce?: Record<string, unknown>
        /** Overrides the provider's `chat_experience` for this event. For a
         *  call made in the same commit that changed the attribution — the
         *  provider only sees the new value on the next render. */
        chatExperience?: ChatExperience
      }
    ) => {
      const __dupe_temp_user_id = tempUserId
      const __dupe_auth_user_id = authUser?.uid

      const _ga = getCookie('_ga')
      const _gcl_aw = getCookie('_gcl_aw')

      if (__dupe_temp_user_id) {
        properties = {
          ...properties,
          __dupe_temp_user_id,
        }
      }

      if (__dupe_auth_user_id) {
        properties = {
          ...properties,
          __dupe_auth_user_id,
        }
      }

      if (_ga) {
        properties = {
          ...properties,
          _ga,
        }
      }

      if (_gcl_aw) {
        properties = {
          ...properties,
          _gcl_aw,
        }
      }

      const stampedChatExperience = options?.chatExperience ?? chatExperience
      if (stampedChatExperience) {
        properties = {
          ...properties,
          chat_experience: stampedChatExperience,
        }
      }

      // Canonical merchant identity (affiliate attribution audit 2026-08):
      // events carry merchant hints under four legacy names (`shopKey`,
      // `shop_key`, `merchant`, `retailer`, …) in four value shapes, which
      // made EPC-by-merchant queries a manual investigation. Resolve whatever
      // hint this payload has into ONE normalized `merchant_key` so every
      // sink gets a single joinable property. Additive only — legacy keys
      // stay untouched (live experiment metrics filter on them).
      const merchantKey = deriveMerchantKey(properties)
      if (merchantKey) {
        properties = {
          ...properties,
          merchant_key: merchantKey,
        }
      }

      // Commerce properties (2026-08 lifecycle discovery). Same additive
      // pattern as `merchant_key` above, for the same reason: the value is
      // already in the payload but buried in a nested shape no sink flattens.
      // Every click emitter passes the whole match (`result: match`), whose
      // `price` is a `{ value, extracted_value, currency }` triple — so a
      // question as basic as "what did the clicked product cost" needed a
      // per-event JSON dig. One derivation here fixes all eighteen emitters.
      const priceProps = derivePriceProps(properties)
      if (priceProps) {
        properties = {
          ...properties,
          ...priceProps,
        }
      }

      // Outbound click identity. `click_id` is minted by the card that built
      // the redirect URL and is the join key into `link_clicks` (and from
      // there to revenue); `short_id` only appears on surfaces that resolve
      // the affiliate URL client-side. See packages/common/analyticsCommerce.ts
      // for the full chain, and why the last hop is `lc_short_id`, never
      // `strackr_transactions.user_id`.
      const clickIdProps = deriveClickIdProps(properties)
      if (clickIdProps) {
        properties = {
          ...properties,
          ...clickIdProps,
        }
      }

      // snake_case twins for camelCase keys on lifecycle events; see
      // packages/common/analyticsPropertyNames.ts for why both spellings ship.
      const snakeCaseTwins = deriveSnakeCaseTwins(event, properties)
      if (snakeCaseTwins) {
        properties = {
          ...properties,
          ...snakeCaseTwins,
        }
      }

      // Dev short-circuit: skip the real network sends, but log the
      // FULLY-MERGED payload (after the temp-user / auth / _ga / _gcl_aw /
      // chat_experience merges above) so the console shows exactly what prod
      // would ship to each sink — plus which sinks this call routes to.
      // NOTE: PostHog super properties (e.g. `default_sort_variant` registered
      // in useSortCriteriaState) are merged by posthog-js at capture time,
      // not here, so they won't appear in this log; explicit per-event fields
      // (sort_criteria, default_sort_variant passed in the payload) will.
      // Shared Meta Pixel ↔ CAPI dedup id — only minted when the event goes
      // to Facebook, and threaded into both the pixel call and the Zaraz
      // payload so Meta collapses the two copies during dual-fire.
      const fbEventId = options?.sendToFb ? generateAnalyticsEventId() : null

      // GA / Zaraz / Meta / Plausible get the payload WITHOUT the shopper's
      // free text. PostHog and Tinybird get all of it — see FIRST_PARTY_ONLY_KEYS.
      const marketingProperties = stripFirstPartyOnly(properties)

      if (process.env.NODE_ENV === 'development') {
        console.log('%c****** useAnalytics.track', 'color:blue', {
          event,
          properties,
          // What GA/Zaraz/Meta/Plausible actually receive — only differs when
          // the payload carried free text (see FIRST_PARTY_ONLY_KEYS).
          ...(marketingProperties !== properties
            ? { marketingProperties }
            : {}),
          ...(options?.personSet || options?.personSetOnce
            ? {
                personSet: options.personSet,
                personSetOnce: options.personSetOnce,
              }
            : {}),
          sinks: {
            ga: !options?.skipGA,
            zaraz: !options?.skipGA && isZarazTrackEnabled(),
            posthog: !options?.skipPosthog,
            fb: !!options?.sendToFb,
            plausible: !!options?.sendToPlausible,
            tinybird: !!options?.sendToTinybird,
            podscribe: !!options?.sendToPodscribe,
          },
        })

        return
      }

      // Zaraz (edge fan-out to GA4/Meta managed components) — replaces the
      // old direct gtag/sendGAEvent call now that <GoogleAnalytics> is gone
      // (docs/features/active/zaraz-migration/). `skipGA` is kept as the
      // option name since call sites still opt out of "the GA4 sink" as a
      // concept; it now routes there via the edge instead of the browser.
      if (!options?.skipGA) {
        zarazTrack(event, {
          ...marketingProperties,
          ...(fbEventId ? { event_id: fbEventId } : {}),
        })
      }

      // PostHog
      if (!options?.skipPosthog) {
        const personOptions =
          options?.personSet || options?.personSetOnce
            ? {
                ...(options.personSet ? { $set: options.personSet } : {}),
                ...(options.personSetOnce
                  ? { $set_once: options.personSetOnce }
                  : {}),
              }
            : undefined
        // Anonymous visitors have no PostHog person (identified_only), so a
        // `$set_once` on their event is dropped server-side. Hold it until
        // `identify`; see pendingPersonProps.ts.
        if (
          options?.personSetOnce &&
          posthog.get_property?.('$user_state') !== 'identified'
        ) {
          stashPendingPersonSetOnce(options.personSetOnce)
        }
        if (personOptions) {
          posthog.capture(event, properties, personOptions)
        } else {
          posthog.capture(event, properties)
        }
      }

      // Facebook Pixel — guard on the queue stub existing: this can run
      // from a mount effect before the afterInteractive stub script executes
      if (options?.sendToFb && typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', event, marketingProperties, {
          eventID: fbEventId,
        })
      }

      // Plausible
      // Custom events are *interactive by default*, and an interactive event in
      // a single-pageview session stops that visit being counted as a bounce
      // (https://plausible.io/docs/metrics-definitions#bounce-rate). Set
      // sendToPlausible:true on any click-out / link that leaves the page —
      // especially monetized convertToRedirectUrl `/redirect` links, which are
      // internal URLs that the provider's `trackOutboundLinks` never sees.
      // Do NOT pass `interactive: false`: that would keep the false bounce
      // (https://plausible.io/docs/custom-event-goals). See apps/web/AGENTS.md.
      if (options?.sendToPlausible) {
        plausible(event, {
          props: marketingProperties,
        })
      }

      // Tinybird
      if (
        options?.sendToTinybird &&
        typeof window !== 'undefined' &&
        window?.Tinybird
      ) {
        window.Tinybird.trackEvent(event, properties || {})
      }

      // Podscribe
      if (options?.sendToPodscribe && window?.podscribe) {
        const hashedEmail = authUser?.email
          ? stringToHash(authUser.email)
          : null

        window?.podscribe?.('lead', {
          device_id: authUser?.uid || tempUserId,
          hashed_email: hashedEmail,
        })
      }
    },
    [plausible, posthog, tempUserId, authUser, chatExperience]
  )

  const identify = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      // const alreadyIdentified = posthog._isIdentified()

      // if (alreadyIdentified) {
      //   logger.debug('🔍🔍🔍 Already identified - skipping', {
      //     userId,
      //     properties,
      //   })

      //   return
      // }

      // if (process.env.NODE_ENV === 'development') {
      //   // console.log('%c****** useAnalytics.identify', 'color:purple', {
      //   //   userId,
      //   //   properties,
      //   // })

      //   return
      // }

      // Identify for monitoring
      datadogRum.setUser({
        id: userId,
        ...properties,
      })

      // PostHog Identify
      // The email holdout goes on at identify (not only at opt-in) so every
      // existing person gets it on their next visit. `$set_once`: the first
      // value wins. Keyed on the email only, so a phone sign-up does not lock
      // in a value before it has an address (see packages/common/holdout.ts).
      const holdout = holdoutFlagFor({ email: properties?.email })
      const pendingSetOnce = takePendingPersonSetOnce()
      posthog.identify(
        userId,
        properties,
        holdout === null
          ? pendingSetOnce
          : { ...pendingSetOnce, [HOLDOUT_FLAG]: holdout }
      )

      // Airbridge - disabled, not in use
      // import('airbridge-web-sdk-loader').then(({ default: airbridge }) => {
      //   airbridge.setUserID(userId)
      // })

      // Intercom — queue until the lazily-activated messenger boots
      const intercomUpdate: IntercomUpdateArgs = {
        userId,
        email: properties?.email,
        phone: properties?.phone,
        customAttributes: {
          tempUserIds: properties?.tempUserIds,
        },
      }
      if (intercomBooted) {
        update(intercomUpdate)
      } else {
        pendingIntercomUpdateRef.current = intercomUpdate
      }
    },
    [posthog, update, intercomBooted]
  )

  const getPostHogDistinctId = useCallback(() => {
    try {
      return posthog?.get_distinct_id()
    } catch (_error: unknown) {
      return null
    }
  }, [posthog])

  const analyticsProxy = {
    identify,
    track,
    getPostHogDistinctId,
  }

  return analyticsProxy
}

export default useAnalytics
